import React, { useState, useRef, useMemo, useEffect } from "react";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import { ChevronDown } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface SelectProps {
  options: Option[];
  value: string | null;
  onChange: (value: string) => void;
  handleSelect?: (value: string) => void;
  placeholder?: string;
  align?: "left" | "right";
}

const CustomSelect: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  handleSelect = () => {},
  placeholder = "Select an option",
  align = "right",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClick(dropdownRef, () => setIsOpen(false));

  const selectedLabel = useMemo(
    () => options.find((opt) => opt.value === value)?.label,
    [options, value]
  );

  const handleSelection = (option: string) => {
    handleSelect(option);
    onChange(option);
    setIsOpen(false);
  };

  // Reset focused index when menu opens
  useEffect(() => {
    if (isOpen) {
      const initialIndex = value
        ? options.findIndex((opt) => opt.value === value)
        : 0;
      setFocusedIndex(initialIndex >= 0 ? initialIndex : 0);
    }
  }, [isOpen, value, options]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (focusedIndex >= 0) {
        handleSelection(options[focusedIndex].value);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div
      className="relative w-auto"
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
      tabIndex={0} // allows focus for keyboard
    >
      <button
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        role="combobox"
        className="w-full flex justify-between gap-3 items-center rounded-lg focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="whitespace-nowrap text-sm font-medium">
          {selectedLabel || placeholder}
        </span>
        <span
          className={`transform transition-all duration-500 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <ChevronDown size={16} />
        </span>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className={`absolute scrollbar-hide ${
            align === "left" ? "left-0" : "right-0"
          } w-auto mt-2 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto z-10`}
        >
          {options.map((option, idx) => (
            <li
              role="option"
              aria-selected={value === option.value}
              key={option.value}
              className={`px-4 py-2 whitespace-nowrap cursor-pointer hover:bg-gray-100 ${
                value === option.value ? "bg-gray-200" : ""
              } ${idx === focusedIndex ? "bg-gray-300" : ""}`}
              onClick={() => handleSelection(option.value)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomSelect;
